/**
 * PlanCard — single source of truth for pricing card rendering.
 *
 * Used by both `/pricing` (PricingPage) and the landing-page teaser.
 * All displayed values come from the `subscription_plans` row; the caller
 * only decides visual variant, click handling, and whether the card
 * shows the "current plan" / "upgrade required" state.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  formatPrice,
  formatStorage,
  formatTrainingRuns,
  type LocalizedText,
  type SubscriptionPlan,
} from '@/services/subscriptionService';

export type PlanCardVariant = 'pricing' | 'landing';

type PlanCardProps = {
  plan: SubscriptionPlan;
  variant: PlanCardVariant;
  /** The price to render (monthly or yearly — resolved by the caller). */
  price: number;
  /** How to label the price period. Pass null for hourly/custom plans. */
  pricePeriodLabel: string | null;
  /** Optional yearly-savings percentage — rendered under the price. */
  savingsPercent?: number | null;
  /** True when this is the subscriber's active plan. */
  isCurrent?: boolean;
  /** Upgrade-only plans: locked when user isn't on an eligible tier. */
  upgradeOnlyLocked?: boolean;
  /** Called when the primary CTA is clicked. `null` → default navigation. */
  onSelect?: (plan: SubscriptionPlan) => void;
  /** Registration href for landing variant (unauthenticated flow). */
  registerHref?: string;
};

function pickLocale(value: LocalizedText | string | null | undefined, lang: string): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] ?? value.en ?? Object.values(value)[0] ?? '';
}

function Check({ on, variant }: { on: boolean; variant: PlanCardVariant }) {
  const activeColor = variant === 'landing' ? 'text-brand-teal' : 'text-green-500';
  const inactiveColor = variant === 'landing' ? 'text-brand-fog/40' : 'text-muted-foreground/40';
  return (
    <svg
      className={`mr-2 mt-0.5 h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5 sm:mr-3 ${on ? activeColor : inactiveColor}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      {on ? (
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      ) : (
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      )}
    </svg>
  );
}

export function PlanCard({
  plan,
  variant,
  price,
  pricePeriodLabel,
  savingsPercent = null,
  isCurrent = false,
  upgradeOnlyLocked = false,
  onSelect,
  registerHref,
}: PlanCardProps) {
  const { t, i18n } = useTranslation('pricing');
  const lang = i18n.language?.slice(0, 2) || 'en';

  const displayName = pickLocale(plan.display_name, lang) || plan.name;
  const description = pickLocale(plan.description, lang);
  const badge = pickLocale(plan.badge_label, lang)
    || (plan.badge_type === 'popular' ? (t('pricing.popular', 'Most Popular') as string) : '');

  const isFeatured = plan.badge_type === 'popular' || plan.badge_type === 'recommended';
  const isHourly = plan.plan_type === 'hourly';
  const isContactCta = plan.cta_type === 'contact' || isHourly;

  // ---- Feature rows (always 6 across all plans for alignment) ----
  const features: { label: ReactNode; on: boolean }[] = [
    {
      on: (plan.total_compute_hours ?? 0) > 0,
      label: (plan.total_compute_hours ?? 0) > 0
        ? <><strong>{(plan.total_compute_hours ?? 0) * 60} min</strong> {t('pricing.feature.computeMinutes', 'compute minutes')}</>
        : t('pricing.feature.computeHoursUnavailable', 'No compute hours'),
    },
    {
      on: plan.max_storage_gb > 0,
      label: plan.max_storage_gb > 0
        ? <><strong>{formatStorage(plan.max_storage_gb)}</strong> {t('pricing.feature.storage', 'cloud storage')}</>
        : t('pricing.feature.storageUnavailable', 'No cloud storage'),
    },
    {
      on: plan.max_file_size_mb !== 0,
      label: plan.max_file_size_mb < 0
        ? <><strong>{t('pricing.feature.fileSizeUnlimited', 'Unlimited')}</strong> {t('pricing.feature.fileSize', 'max file size')}</>
        : plan.max_file_size_mb > 0
          ? <><strong>{plan.max_file_size_mb} MB</strong> {t('pricing.feature.fileSize', 'max file size')}</>
          : t('pricing.feature.fileSizeUnavailable', 'No file uploads'),
    },
    {
      on: plan.can_use_data_preparation,
      label: t('pricing.feature.dataPreparation', 'Data preparation'),
    },
    {
      on: plan.can_use_training,
      label: plan.can_use_training
        ? `${formatTrainingRuns(plan.max_training_runs_per_month, t)} ${t('pricing.feature.training', 'model training')}`
        : (t('pricing.feature.trainingUnavailable', 'No training') as string),
    },
    {
      on: plan.has_api_access,
      label: plan.has_api_access
        ? plan.max_parallel_apis > 1
          ? t('pricing.feature.apiParallel', 'Up to {{count}} parallel APIs', { count: plan.max_parallel_apis })
          : (t('pricing.feature.apiSingle', '1 live API') as string)
        : (t('pricing.feature.apiUnavailable', 'No API access') as string),
    },
    {
      on: plan.priority_support,
      label: plan.priority_support
        ? (t('pricing.feature.prioritySupport', 'Priority support') as string)
        : (t('pricing.feature.standardSupport', 'Standard support') as string),
    },
  ];

  // ---- Variant-specific styling ----
  const containerClasses = variant === 'landing'
    ? [
        'relative flex h-full flex-col p-8 lg:p-10',
        isFeatured
          ? 'bg-brand-paper text-brand-ink'
          : 'bg-brand-navy/40 text-brand-paper',
      ].join(' ')
    : [
        'relative flex h-full flex-col bg-card rounded-2xl shadow-xl transition-transform hover:scale-105',
        isFeatured ? 'ring-2 ring-primary' : '',
        upgradeOnlyLocked ? 'opacity-75' : '',
      ].join(' ');

  const bodyClasses = variant === 'landing'
    ? 'flex flex-col flex-1'
    : 'p-4 sm:p-6 lg:p-8 flex flex-col flex-1';

  const badgeEl = badge && (
    variant === 'landing' ? (
      <span className="absolute -top-3 left-8 rounded-sm bg-brand-teal px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
        {badge}
      </span>
    ) : (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
          {badge}
        </span>
      </div>
    )
  );

  const priceEl = isHourly
    ? (t('pricing.onRequest', 'On request') as string)
    : price === 0
      ? (t('pricing.free', 'Free') as string)
      : formatPrice(price);

  const ctaLabel = pickLocale(plan.cta_label, lang)
    || (isContactCta
      ? (t('pricing.contactUs', 'Contact us') as string)
      : price === 0
        ? (t('pricing.getStarted', 'Get Started') as string)
        : (t('pricing.selectPlan', 'Select Plan') as string));

  // ---- CTA rendering ----
  const renderCta = () => {
    // 1. Current plan — disabled "Current Plan" button
    if (isCurrent) {
      return (
        <button disabled className={
          variant === 'landing'
            ? 'inline-flex h-11 items-center gap-2 rounded-sm px-5 text-[13px] font-semibold tracking-tight bg-brand-ink/10 text-brand-ink/60 cursor-not-allowed'
            : 'w-full py-3 px-6 rounded-lg font-semibold bg-muted text-muted-foreground cursor-not-allowed'
        }>
          {t('pricing.currentPlan', 'Current Plan')}
        </button>
      );
    }

    // 2. Contact / mailto — anchor tag
    if (isContactCta) {
      return (
        <a
          href="mailto:info@forecast-engine.com"
          className={
            variant === 'landing'
              ? 'inline-flex h-11 items-center gap-2 rounded-sm px-5 text-[13px] font-semibold tracking-tight transition-all hover:-translate-y-0.5 bg-brand-navy text-brand-paper hover:bg-brand-navy2'
              : 'block w-full py-3 px-6 rounded-lg font-semibold text-center bg-foreground text-background hover:bg-foreground/90 transition-all'
          }
        >
          {ctaLabel}
          {variant === 'landing' && <span>→</span>}
        </a>
      );
    }

    // 3. Upgrade-only locked — disabled button with model-requirement hint (#131)
    if (upgradeOnlyLocked && variant === 'pricing') {
      return (
        <div className="space-y-2">
          <button
            disabled
            className="w-full py-3 px-6 rounded-lg font-semibold bg-muted text-muted-foreground cursor-not-allowed"
            title={t('pricing.apiOnlyLocked', 'Available after you train at least one model.') as string}
          >
            {t('pricing.upgradeOnlyLocked', 'Upgrade required') as string}
          </button>
          <p className="text-xs text-muted-foreground text-center">
            {t('pricing.apiOnlyLocked', 'Available after you train at least one model.') as string}
          </p>
        </div>
      );
    }

    // 4. Default — either call onSelect handler (pricing) or route to register (landing)
    if (onSelect) {
      return (
        <button
          onClick={() => onSelect(plan)}
          className={
            variant === 'landing'
              ? `inline-flex h-11 items-center gap-2 rounded-sm px-5 text-[13px] font-semibold tracking-tight transition-all hover:-translate-y-0.5 ${isFeatured ? 'bg-brand-navy text-brand-paper hover:bg-brand-navy2' : 'border border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-brand-navy'}`
              : `w-full py-3 px-6 rounded-lg font-semibold transition-all ${isFeatured ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg' : 'bg-foreground text-background hover:bg-foreground/90'}`
          }
        >
          {ctaLabel}
          {variant === 'landing' && <span>→</span>}
        </button>
      );
    }

    // 5. Landing fallback — link to registration with plan preselected
    return (
      <Link
        to={registerHref || (plan.slug ? `/register?plan=${encodeURIComponent(plan.slug)}` : '/register')}
        className={
          variant === 'landing'
            ? `inline-flex h-11 items-center gap-2 rounded-sm px-5 text-[13px] font-semibold tracking-tight transition-all hover:-translate-y-0.5 ${isFeatured ? 'bg-brand-navy text-brand-paper hover:bg-brand-navy2' : 'border border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-brand-navy'}`
            : `w-full block text-center py-3 px-6 rounded-lg font-semibold transition-all ${isFeatured ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg' : 'bg-foreground text-background hover:bg-foreground/90'}`
        }
      >
        {ctaLabel}
        {variant === 'landing' && <span>→</span>}
      </Link>
    );
  };

  // ---- Render ----
  return (
    <div className={containerClasses}>
      {badgeEl}

      <div className={bodyClasses}>
        <div className={variant === 'landing' ? 'flex items-baseline justify-between' : ''}>
          <h3 className={
            variant === 'landing'
              ? 'display text-[30px] leading-none tracking-tight'
              : 'text-xl sm:text-2xl font-bold text-foreground mb-2 break-words'
          }>
            {displayName}
          </h3>
          {variant === 'landing' && (
            <span className={`font-mono text-[12px] ${isFeatured ? 'text-brand-ink/60' : 'text-brand-fog'}`}>
              {price > 0 && pricePeriodLabel ? `/${pricePeriodLabel}` : ''}
            </span>
          )}
        </div>

        {variant === 'pricing' && description && (
          <p className="text-sm text-muted-foreground mb-4 break-words leading-relaxed min-h-[96px]">
            {description}
          </p>
        )}

        <div className={variant === 'landing' ? 'mt-5 flex items-baseline gap-2' : 'mb-6'}>
          <span className={
            variant === 'landing'
              ? 'display-tight text-[58px] leading-none'
              : 'text-3xl sm:text-4xl font-bold text-foreground break-words'
          }>
            {priceEl}
          </span>
          {variant === 'pricing' && !isHourly && price > 0 && pricePeriodLabel && (
            <span className="text-muted-foreground ml-1 sm:ml-2 text-sm sm:text-base">
              /{pricePeriodLabel}
            </span>
          )}
        </div>

        {variant === 'pricing' && savingsPercent && savingsPercent > 0 && !isHourly && (
          <div className="text-sm text-green-600 font-semibold mb-2">
            {t('pricing.savingsLabel', 'Save {{savings}}%', { savings: savingsPercent })}
          </div>
        )}

        {variant === 'landing' && (
          <p className={`mt-6 min-h-[96px] max-w-[36ch] text-[14.5px] leading-[1.7] ${isFeatured ? 'text-brand-ink/75' : 'text-brand-fog'}`}>
            {description}
          </p>
        )}

        <ul className={
          variant === 'landing'
            ? `mt-6 flex-grow space-y-2 text-[13.5px] leading-[1.5] ${isFeatured ? 'text-brand-ink/80' : 'text-brand-paper/85'}`
            : 'space-y-3 sm:space-y-4 mb-8 flex-grow'
        }>
          {features.map((f, i) => (
            <li key={i} className="flex items-start min-w-0">
              <Check on={f.on} variant={variant} />
              <span className={
                variant === 'landing'
                  ? (f.on ? '' : 'opacity-60')
                  : `text-sm sm:text-base break-words ${f.on ? 'text-foreground' : 'text-muted-foreground'}`
              }>
                {f.label}
              </span>
            </li>
          ))}
        </ul>

        <div className={variant === 'landing' ? 'mt-8' : ''}>
          {renderCta()}
        </div>
      </div>
    </div>
  );
}

export default PlanCard;
